import React, { forwardRef, ReactElement, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { LazyLoadComponent } from 'react-lazy-load-image-component';
import { InView, useInView } from 'react-intersection-observer';
import stickybits from 'stickybits';
import { merge } from 'lodash';
import { makeId } from '../../../utils/helper/helpers';
import { DeviceDetector } from '../../../utils/helper/browserDetection';
import { getValue } from '../../../utils/helper/styling';
import { IEErrorBoundary } from '../error/Error';
import { Position } from '../../../config/models';
import { useGlobal } from '../../../logic/main';

import {
    AbsoluteInner,
    AbsoluteWrapper,
    Description,
    DescriptionProps,
    Empty,
    Padding,
    SlidesWrapper,
    Sticky,
    Wrapper
} from './styles.ScrollyTeller';
import { boardConfig } from '../../../config/boardConfig';

export type StepTrigger<T = any> = (currentStep: number, id: string, direction: Direction, trigger?: T) => void;

interface Props<T = any> {
    onStepEnter?: StepTrigger;
    onStepLeave?: StepTrigger;
    initialPadding?: boolean;
    onLastStepLeave?: () => void;
    onEnter?: (direction: Direction) => void;
    onLeave?: (direction: Direction) => void;
    onAllTriggersLoaded?: (triggers: any[]) => void;
    triggerOnHit?: boolean;
    isFixed?: boolean;
    lazyLoad?: boolean;
    onScroll?: (top: number) => void;
    useFullHeight?: boolean;
    wrapperWidth?: string;
    padding?: number[] | number;
    stickyWidth?: string;
    stickyPosition?: Position;
    stickyElement: React.ReactNode;
    descriptionWidth?: string;
    descriptionPosition?: Position;
    descriptionConfig?: DescriptionProps<T>;
    style?: React.CSSProperties;
}

export const SCROLLY_STICKY_WRAPPER = 'sticky-scrolly';
export const SCROLLY_MAIN_WRAPPER = 'scroll-wrapper';
export const SCROLLY_SLIDES_WRAPPER = 'slides-wrapper';

export interface ScrollyProps {
    children: ReactElement<typeof Description | typeof Empty>[];
}

export interface ScrollyTellerRef {
    skipStep: () => void;
}

export enum Direction {
    UP = 'UP',
    DOWN = 'DOWN'
}

export const ScrollyTeller: React.FC<Props> = forwardRef<ScrollyTellerRef, Props>(
    (
        {
            onStepEnter,
            onEnter,
            onLeave,
            onStepLeave,
            onScroll,
            onLastStepLeave,
            triggerOnHit,
            lazyLoad = true,
            useFullHeight = false,
            wrapperWidth,
            padding,
            stickyWidth,
            stickyPosition,
            stickyElement,
            descriptionWidth,
            descriptionPosition,
            descriptionConfig,
            style,
            isFixed,
            onAllTriggersLoaded,
            initialPadding = true,
            children
        },
        fwRef
    ) => {
        const [navbarHeight] = useGlobal(state => state.navbarHeight);
        const [inViewRef, inView] = useInView({ threshold: 0.8 });
        let direction = useRef(Direction.DOWN);
        const [currentStep, setCurrentStep] = useState<number>(0);
        const [scrolledAlready, setScrolledAlready] = useState(false);
        const [stickyFixedWidth, setStickyFixedWidth] = useState(stickyWidth);
        const [allTriggersAnnounced, setAllTriggersAnnounced] = useState(false);
        const scrollyWrapperRef = useRef<HTMLDivElement>();
        const [id] = useState(makeId(8));

        let { current: lastScroll } = useRef<number>(0);
        let { current: allTriggers } = useRef<any[]>([]);

        function handleChildInView(index: number, id: string, trigger: any, childInView: boolean) {
            if (childInView) {
                onStepEnter && onStepEnter(index, id, direction.current, trigger || {});
                if (currentStep !== index) {
                    setCurrentStep(index);
                }
            } else if (scrolledAlready) {
                onStepLeave && onStepLeave(index, id, direction.current, trigger || {});
                index === React.Children.count(children) - 1 && onLastStepLeave && onLastStepLeave();
            }
        }

        useEffect(() => {
            if (inView) {
                onEnter && onEnter(direction.current);
                if (!boardConfig.listensOnScroll) {
                    document.addEventListener('scroll', handleScroll as any);
                    boardConfig.listensOnScroll = true;
                }
            } else {
                onLeave && onLeave(direction.current);
                document.removeEventListener('scroll', handleScroll as any);
                boardConfig.listensOnScroll = false;
            }
        }, [inView]);

        const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
            const scrollPos = isFixed
                ? scrollyWrapperRef.current?.getBoundingClientRect().top || 0
                : e.currentTarget.scrollTop || 0;
            if (onScroll && scrollyWrapperRef.current) {
                onScroll(scrollPos);
            }
            !scrolledAlready && setScrolledAlready(true);

            if (scrollPos >= lastScroll) {
                direction.current = Direction.UP;
            } else {
                direction.current = Direction.DOWN;
            }
            lastScroll = scrollPos;
        }, []);

        const getPadding = (index: number) =>
            typeof padding === 'number' ? padding : !!padding && padding[index] ? padding[index] : innerHeight;

        useImperativeHandle(fwRef, () => ({
            skipStep() {
                const nextSlide = document.getElementById(`slide-${currentStep + 1}`);
                if (nextSlide) {
                    nextSlide.scrollIntoView({ block: 'end' });
                }
            }
        }));

        const getTriggerMargin = () => {
            const sticky = document.getElementById(id);
            const offset = (sticky?.clientHeight || 0) + getValue(navbarHeight) - innerHeight;

            if (DeviceDetector.isInternetExplorer) {
                return '0px';
            } else {
                return `0px 0px ${triggerOnHit ? offset : 0}px 0px`;
            }
        };

        const onLoad = () => {
            if (DeviceDetector.isInternetExplorer) {
                setStickyFixedWidth(
                    document.getElementsByClassName(`${SCROLLY_STICKY_WRAPPER}`)[0].getBoundingClientRect().width + 'px'
                );
                stickybits(`.${SCROLLY_STICKY_WRAPPER}`).update({ stickyBitStickyOffset: parseInt(navbarHeight + 20) });
            }
        };

        return (
            <IEErrorBoundary>
                <LazyLoadComponent
                    afterLoad={onLoad}
                    visibleByDefault={!lazyLoad}
                    style={useFullHeight ? { minHeight: '100vh' } : undefined}
                >
                    <Wrapper
                        width={wrapperWidth}
                        ref={scrollyWrapperRef as any}
                        style={
                            DeviceDetector.isInternetExplorer
                                ? { ...style, marginBottom: React.Children.count(children) * 150 + 'vh' }
                                : { ...style }
                        }
                        className={SCROLLY_MAIN_WRAPPER}
                        onScroll={!isFixed ? handleScroll : undefined}
                    >
                        <Sticky
                            id={id}
                            ref={inViewRef}
                            useFullHeight={useFullHeight}
                            position={stickyPosition}
                            navbarHeight={navbarHeight}
                            width={stickyFixedWidth}
                            className={SCROLLY_STICKY_WRAPPER}
                        >
                            {stickyElement}
                        </Sticky>
                        <SlidesWrapper
                            position={descriptionPosition}
                            width={descriptionWidth}
                            id={SCROLLY_SLIDES_WRAPPER}
                        >
                            {initialPadding ? (
                                <Padding height={(document.getElementById(id)?.clientHeight || 0) / 2 || 0} />
                            ) : null}
                            {React.Children.map(
                                children as any,
                                (child: typeof Description & ReactElement<any, any>, index) => {
                                    let childProps: any = child.props;
                                    try {
                                        const hocProps = typeof child.type === 'function' ? child.type({}).props : {};
                                        childProps = merge(hocProps, childProps);
                                    } catch (e) {
                                        console.error(e);
                                    }
                                    if (onAllTriggersLoaded && !allTriggersAnnounced) {
                                        if (allTriggers.length <= React.Children.count(children)) {
                                            allTriggers.push(childProps.trigger);
                                        } else {
                                            setAllTriggersAnnounced(true);
                                            onAllTriggersLoaded(allTriggers);
                                        }
                                    }

                                    return (
                                        <>
                                            <InView
                                                rootMargin={getTriggerMargin()}
                                                onChange={handleChildInView.bind(
                                                    undefined,
                                                    index,
                                                    childProps.id,
                                                    childProps.trigger
                                                )}
                                                className="slide"
                                                style={{
                                                    pointerEvents:
                                                        child.type.displayName === 'stylesScrollyTeller__Empty'
                                                            ? 'none'
                                                            : 'auto'
                                                }}
                                                id={`slide-${index}`}
                                            >
                                                {React.cloneElement(child, { ...descriptionConfig, ...child.props })}
                                            </InView>
                                            {!child.props.noPadding ? (
                                                <Padding
                                                    height={
                                                        child.props.paddingHeight
                                                            ? child.props.paddingHeight
                                                            : React.Children.count(children) - 1 === index
                                                            ? innerHeight
                                                            : getPadding(index)
                                                    }
                                                    lineWidth={descriptionConfig?.lineWidth}
                                                    lineColor={descriptionConfig?.lineColor}
                                                />
                                            ) : null}
                                        </>
                                    );
                                }
                            )}
                        </SlidesWrapper>
                    </Wrapper>
                </LazyLoadComponent>
            </IEErrorBoundary>
        );
    }
);

export const AbsoluteChild: React.FC = ({ children }) => (
    <AbsoluteWrapper>
        <AbsoluteInner>{children}</AbsoluteInner>
    </AbsoluteWrapper>
);
